import { createContext, useState, useContext, useEffect } from 'react';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation debe usarse dentro de LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [userLocation, setUserLocation] = useState(() => {
    // Cargar ubicación desde localStorage al iniciar
    const saved = localStorage.getItem('userLocation');
    return saved ? JSON.parse(saved) : null;
  });

  // Guardar en localStorage cuando cambia
  useEffect(() => {
    if (userLocation) {
      localStorage.setItem('userLocation', JSON.stringify(userLocation));
    } else {
      localStorage.removeItem('userLocation');
    }
  }, [userLocation]);

  /**
   * Guarda la ubicación del usuario
   * @param {Object} location - Objeto con provincia, localidad, codigoPostal, etc.
   */
  const saveLocation = (location) => {
    const locationData = {
      provincia: location.provincia,
      localidad: location.localidad || location.departamento || null,
      codigoPostal: location.codigoPostal,
      coordenadas: location.coordenadas || null,
      timestamp: new Date().toISOString()
    };

    setUserLocation(locationData);
  };

  /**
   * Limpia la ubicación guardada
   */
  const clearLocation = () => {
    setUserLocation(null);
  };

  /**
   * Obtiene un texto formateado de la ubicación
   * @returns {string} Texto como "San Luis, Argentina" o "Buenos Aires"
   */
  const getLocationText = () => {
    if (!userLocation) return null;

    if (userLocation.localidad && userLocation.localidad !== userLocation.provincia) {
      return `${userLocation.localidad}, ${userLocation.provincia}`;
    }

    return userLocation.provincia;
  };

  /**
   * Obtiene un texto corto de la ubicación
   * @returns {string} Texto como "San Luis" o "CABA"
   */
  const getShortLocationText = () => {
    if (!userLocation) return null;

    // Si es CABA, mostrar CABA
    if (userLocation.provincia === 'Ciudad Autónoma de Buenos Aires') {
      return 'CABA';
    }

    // Si tiene localidad diferente a la provincia, mostrar localidad
    if (userLocation.localidad && userLocation.localidad !== userLocation.provincia) {
      return userLocation.localidad;
    }

    // Si no, mostrar provincia
    return userLocation.provincia;
  };

  const value = {
    userLocation,
    saveLocation,
    clearLocation,
    getLocationText,
    getShortLocationText,
    hasLocation: !!userLocation
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
